import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Circle, Users, Buildings, SpeakerHigh, SpeakerSlash } from "@phosphor-icons/react";
import Sparkline from "@/components/Sparkline";
import { playNewUserChime, isNewUserChimeMuted, setNewUserChimeMuted } from "@/lib/newUserChime";

const POLL_MS = 15000;
// v1.28.11 — a user whose last_active_at is older than this counts as
// "idle" and shows an amber dot instead of green.
const IDLE_THRESHOLD_S = 120;

const relTime = (iso) => {
    if (!iso) return "";
    const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    if (s < 60) return `${s}s ago`;
    return `${Math.floor(s / 60)}m ago`;
};

const isIdle = (iso) => {
    if (!iso) return false;
    const s = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));
    return s >= IDLE_THRESHOLD_S;
};

/**
 * OnlineUsers — pill in the admin header showing how many users
 * (admins + parties) are currently active. Clicking opens a panel
 * with each name, role, last-active time, and a 24-hour activity
 * sparkline (v1.25.72). For a Super User with no impersonation, the
 * panel also shows the company each user belongs to.
 */
export default function OnlineUsers({ compact = false }) {
    const [data, setData] = useState({ count: 0, users: [], cross_tenant: false });
    const [open, setOpen] = useState(false);
    const [muted, setMuted] = useState(isNewUserChimeMuted());
    const wrapRef = useRef(null);
    // v1.28.11 — Track user IDs seen on the previous poll so we can chime
    // only for genuinely-new arrivals. Seeded from the FIRST successful
    // load so the initial render never fires the chime.
    const seenIdsRef = useRef(null);
    // Tick every 20s so the "idle" amber dot flips on for users who stop
    // heartbeating without the panel needing a fresh fetch.
    const [, setNowTick] = useState(0);

    const load = async () => {
        try {
            const { data: d } = await api.get("/online-users");
            const nextUsers = d?.users || [];
            const nextIds = new Set(nextUsers.map((u) => u.id).filter(Boolean));
            if (seenIdsRef.current === null) {
                // First load — remember who is already online, no chime.
                seenIdsRef.current = nextIds;
            } else {
                const newcomers = [...nextIds].filter((id) => !seenIdsRef.current.has(id));
                if (newcomers.length > 0) {
                    playNewUserChime();
                }
                seenIdsRef.current = nextIds;
            }
            setData(d || { count: 0, users: [], cross_tenant: false });
        } catch (_e) { /* silent polling */ }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, POLL_MS);
        const idleTicker = setInterval(() => setNowTick((n) => n + 1), 20000);
        const onFocus = () => load();
        window.addEventListener("focus", onFocus);
        return () => {
            clearInterval(t);
            clearInterval(idleTicker);
            window.removeEventListener("focus", onFocus);
        };
    }, []);

    const toggleMuted = () => {
        const next = !muted;
        setNewUserChimeMuted(next);
        setMuted(next);
    };

    useEffect(() => {
        if (!open) return;
        const onDoc = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, [open]);

    const count = data.count || 0;
    const crossTenant = !!data.cross_tenant;
    // v1.28.10 — Bucket every online user into 4 groups so the panel count
    // (Online now · N) always equals the sum of names rendered. Previously
    // staff / agent users were dropped from the list but still counted.
    const users = data.users || [];
    const superUsers = users.filter((u) => u.is_super_user);
    const admins = users.filter((u) => !u.is_super_user && u.role === "admin");
    const dataEntryOps = users.filter(
        (u) => !u.is_super_user && u.role === "staff" && u.staff_permission === "data_entry_operator",
    );
    const otherStaff = users.filter(
        (u) => !u.is_super_user && u.role === "staff" && u.staff_permission !== "data_entry_operator",
    );
    const parties = users.filter((u) => !u.is_super_user && (u.role === "customer" || u.role === "agent"));
    const rendered = superUsers.length + admins.length + dataEntryOps.length + otherStaff.length + parties.length;
    const staffPermLabel = (perm) => {
        if (perm === "full_access") return "Full Access";
        if (perm === "view_only") return "View Only";
        if (perm === "upload_only") return "Upload Only";
        return "Staff";
    };

    const renderRow = (u, kind) => {
        const primary = kind === "party" ? (u.party_name || u.name) : (u.name || u.email);
        const secondary = kind === "party" ? (u.phone || u.email) : (u.email || u.phone);
        // v1.28.10 — Sparkline colour keyed per section so at-a-glance
        // scanning still works with 4 groups instead of 2.
        const sparkColor =
            kind === "super_user" ? "#d97706" :
            kind === "deo" ? "#0d9488" :
            kind === "staff" ? "#9333ea" :
            kind === "admin" ? "#4f46e5" :
            "#059669";
        // v1.28.11 — Idle indicator: amber dot when the user's last
        // heartbeat is older than 2 minutes.
        const idle = isIdle(u.last_active_at);
        const dotColor = idle ? "text-amber-500" : "text-emerald-500";
        return (
            <div key={u.id} data-testid={`online-${kind}-${u.id}`} data-idle={idle ? "1" : "0"} className="flex items-center justify-between gap-2 py-1 text-xs">
                <div className="flex items-center gap-1.5 min-w-0">
                    <Circle size={7} weight="fill" className={`shrink-0 ${dotColor}`} title={idle ? "Idle · no activity in 2+ minutes" : "Active"} />
                    <div className="min-w-0">
                        <div className="truncate font-medium text-slate-800">{primary}</div>
                        <div className="truncate text-[10px] text-slate-400">
                            {secondary}
                            {crossTenant && u.company_name && (
                                <span className="ml-1.5 inline-flex items-center gap-0.5 rounded bg-slate-100 px-1 py-[1px] text-[9px] font-semibold text-slate-600" data-testid={`online-${kind}-company-${u.id}`}>
                                    <Buildings size={8} weight="bold" /> {u.company_name}
                                </span>
                            )}
                        </div>
                    </div>
                </div>
                <div className="flex shrink-0 items-center gap-1.5">
                    <Sparkline
                        data={u.activity_24h || []}
                        width={56}
                        height={16}
                        colorTop={sparkColor}
                        testId={`online-${kind}-spark-${u.id}`}
                    />
                    <span className="w-11 text-right text-[10px] text-slate-400">{relTime(u.last_active_at)}</span>
                </div>
            </div>
        );
    };

    return (
        <div className="relative" ref={wrapRef}>
            <button
                type="button"
                data-testid="online-users-btn"
                onClick={() => setOpen((v) => !v)}
                aria-label={`${count} users online`}
                className={`flex items-center gap-1 rounded-full border border-slate-200 bg-white font-semibold text-slate-700 shadow-sm transition hover:border-emerald-300 hover:bg-emerald-50 ${compact ? "px-2 py-0.5 text-[11px]" : "px-2.5 py-1 text-xs"}`}
            >
                <span className={`relative flex ${compact ? "h-1.5 w-1.5" : "h-2 w-2"}`}>
                    <span className={`absolute inline-flex h-full w-full rounded-full ${count > 0 ? "animate-ping bg-emerald-400 opacity-75" : "bg-slate-300"}`} />
                    <span className={`relative inline-flex rounded-full ${compact ? "h-1.5 w-1.5" : "h-2 w-2"} ${count > 0 ? "bg-emerald-500" : "bg-slate-400"}`} />
                </span>
                <span data-testid="online-users-count">{count}</span>
                {!compact && <span className="hidden sm:inline text-slate-400">online{crossTenant ? " · all tenants" : ""}</span>}
            </button>

            {open && (
                <div
                    data-testid="online-users-panel"
                    className="absolute right-0 top-full z-40 mt-2 w-[22rem] overflow-hidden rounded-xl border border-slate-200 bg-white shadow-xl"
                >
                    <div className="flex items-center justify-between border-b border-slate-100 bg-slate-50 px-4 py-2.5">
                        <div className="flex items-center gap-2 font-display text-sm font-semibold text-slate-900">
                            <Users size={14} weight="bold" /> Online now · {count}
                            {crossTenant && (
                                <span className="ml-1 rounded-full bg-indigo-100 px-1.5 py-[1px] text-[9px] font-bold uppercase tracking-wider text-indigo-700">
                                    All tenants
                                </span>
                            )}
                        </div>
                        <button
                            type="button"
                            data-testid="online-users-mute-btn"
                            onClick={toggleMuted}
                            title={muted ? "New-user chime muted — click to unmute" : "Playing chime on new arrivals — click to mute"}
                            className={`flex h-6 w-6 items-center justify-center rounded-full transition ${muted ? "text-slate-400 hover:bg-slate-200" : "text-emerald-600 hover:bg-emerald-50"}`}
                        >
                            {muted ? <SpeakerSlash size={13} weight="fill" /> : <SpeakerHigh size={13} weight="fill" />}
                        </button>
                    </div>
                    <div className="max-h-[60vh] overflow-y-auto">
                        {rendered === 0 && (
                            <div className="px-4 py-8 text-center text-xs text-slate-400">
                                No one else active right now.
                            </div>
                        )}
                        {superUsers.length > 0 && (
                            <div data-testid="online-section-super_user" className="border-b border-slate-100 px-4 py-2">
                                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-amber-700">Super Users ({superUsers.length})</div>
                                {superUsers.map((u) => renderRow(u, "super_user"))}
                            </div>
                        )}
                        {admins.length > 0 && (
                            <div data-testid="online-section-admin" className="border-b border-slate-100 px-4 py-2">
                                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Admins ({admins.length})</div>
                                {admins.map((u) => renderRow(u, "admin"))}
                            </div>
                        )}
                        {dataEntryOps.length > 0 && (
                            <div data-testid="online-section-deo" className="border-b border-slate-100 px-4 py-2">
                                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-teal-700">Data Entry Operators ({dataEntryOps.length})</div>
                                {dataEntryOps.map((u) => renderRow(u, "deo"))}
                            </div>
                        )}
                        {otherStaff.length > 0 && (
                            <div data-testid="online-section-staff" className="border-b border-slate-100 px-4 py-2">
                                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-purple-700">
                                    Staff ({otherStaff.length})
                                    <span className="ml-1 text-[9px] font-normal text-slate-400">
                                        · {[...new Set(otherStaff.map((s) => staffPermLabel(s.staff_permission)))].join(", ")}
                                    </span>
                                </div>
                                {otherStaff.map((u) => renderRow(u, "staff"))}
                            </div>
                        )}
                        {parties.length > 0 && (
                            <div data-testid="online-section-party" className="px-4 py-2">
                                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wider text-slate-500">Party Login ({parties.length})</div>
                                {parties.map((u) => renderRow(u, "party"))}
                            </div>
                        )}
                    </div>
                    <div className="border-t border-slate-100 bg-slate-50 px-4 py-2 text-center text-[10px] text-slate-400">
                        <span className="inline-flex items-center gap-1"><Circle size={7} weight="fill" className="text-emerald-500" /> Active</span>
                        <span className="mx-1.5">·</span>
                        <span className="inline-flex items-center gap-1"><Circle size={7} weight="fill" className="text-amber-500" /> Idle 2m+</span>
                        <span className="mx-1.5">·</span>
                        Updates every 15s
                    </div>
                </div>
            )}
        </div>
    );
}
